// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useState, useEffect } from 'react';
import axios from '../utils/axiosConfig';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import { useTheme, alpha } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';

const StargateCredentials = ({ stargateId, condensed = false, onShowPythonClient, sx }) => {
  const theme = useTheme();
  const [credentials, setCredentials] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
  });
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    fetchCredentials();
  }, [stargateId]);

  const fetchCredentials = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/stargates/${stargateId}/credentials`);
      setCredentials(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching credentials:', err);
      setError('Failed to load credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateCredentials = async () => {
    try {
      setRegenerating(true);
      const response = await axios.post(`/stargates/${stargateId}/regenerate-credentials`);
      setCredentials(response.data);
      setSnackbar({
        open: true,
        message: 'Credentials regenerated successfully',
        severity: 'success',
      });
    } catch (err) {
      console.error('Error regenerating credentials:', err);
      setSnackbar({
        open: true,
        message: 'Failed to regenerate credentials',
        severity: 'error',
      });
    } finally {
      setRegenerating(false);
    }
  };

  const handleDownloadScript = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/stargates/${stargateId}/mqtt-script`, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      // Extract filename from Content-Disposition header if available
      let filename = `stargate_mqtt_${stargateId}.py`;
      const disposition = response.headers['content-disposition'];
      if (disposition && disposition.indexOf('filename=') !== -1) {
        const match = disposition.match(/filename="?([^;"]+)"?/);
        if (match && match[1]) filename = match[1];
      }
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setSnackbar({
        open: true,
        message: 'Python script downloaded!',
        severity: 'success',
      });
    } catch (err) {
      console.error('Error downloading script:', err);
      setSnackbar({
        open: true,
        message: 'Failed to download Python script. Please check your authentication.',
        severity: 'error',
      });
    }
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(
        () => {
          setSnackbar({
            open: true,
            message: 'Copied to clipboard',
            severity: 'success',
          });
        },
        (err) => {
          setSnackbar({
            open: true,
            message: 'Failed to copy to clipboard',
            severity: 'error',
          });
          console.error('Could not copy text: ', err);
        }
      );
    } else {
      // Fallback for insecure context or unsupported browsers
      try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        setSnackbar({
          open: true,
          message: 'Copied to clipboard',
          severity: 'success',
        });
      } catch (err) {
        setSnackbar({
          open: true,
          message: 'Failed to copy to clipboard',
          severity: 'error',
        });
        console.error('Fallback: Could not copy text: ', err);
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        ...(typeof sx === 'object' ? sx : {}),
      }}
    >
      <CardHeader
        title="Stargate Credentials"
        action={
          <IconButton onClick={fetchCredentials} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        }
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: alpha(theme.palette.primary.main, 0.03),
        }}
      />
      <CardContent>
        {credentials && (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                MQTT Username
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" fontFamily="monospace" sx={{ mr: 1 }}>
                  {credentials.mqtt_username}
                </Typography>
                <IconButton size="small" onClick={() => copyToClipboard(credentials.mqtt_username)}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Box>
              {credentials.auth_method === 'password' && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                    MQTT Password
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" fontFamily="monospace" sx={{ mr: 1 }}>
                      {credentials.mqtt_password}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => copyToClipboard(credentials.mqtt_password)}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </>
              )}
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                Broker Address
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" fontFamily="monospace" sx={{ mr: 1 }}>
                  {credentials.broker_address}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => copyToClipboard(credentials.broker_address)}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                Broker Port
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" fontFamily="monospace" sx={{ mr: 1 }}>
                  {credentials.broker_port}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => copyToClipboard(credentials.broker_port.toString())}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box
                sx={{
                  mt: 1,
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 1,
                }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleRegenerateCredentials}
                  disabled={regenerating}
                  size="small"
                  startIcon={regenerating ? <CircularProgress size={16} /> : null}
                >
                  {regenerating ? 'Regenerating...' : 'Regenerate'}
                </Button>
                {condensed && (
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={onShowPythonClient}
                  >
                    Python Client
                  </Button>
                )}
              </Box>
            </Box>
            {!condensed && (
              <>
                <Divider sx={{ my: 3 }} />
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Python MQTT Client
                  </Typography>
                  <Typography variant="body2" paragraph>
                    Download a pre-configured Python MQTT client script for this Stargate. This
                    script is ready to run and will automatically connect to the dashboard using the
                    credentials above.
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownloadScript}
                    >
                      Download Python Script
                    </Button>
                  </Box>
                  <Typography variant="subtitle2" gutterBottom>
                    To use the script:
                  </Typography>
                  <Box component="ol" sx={{ pl: 2 }}>
                    <li>
                      <Typography variant="body2">
                        Transfer the script to your Stargate device
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        Make it executable: <code>chmod +x stargate_mqtt_*.py</code>
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        Install dependencies: <code>pip install paho-mqtt</code>
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        Run the script: <code>./stargate_mqtt_*.py</code>
                      </Typography>
                    </li>
                  </Box>
                </Box>
              </>
            )}
          </>
        )}
      </CardContent>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default StargateCredentials;
